<template>
  <div>
    <div
      v-if="$root.user.isAdmin"
      class="d-flex justify-content-between align-items-center mb-3"
    >
      <div>
        <file-upload
          ref="upload"
          v-model="files"
          :data="Object.assign(data, { parent_id: folder.parent_id })"
          :headers="{ Authorization: 'Bearer ' + $auth.token() }"
          :multiple="true"
          :post-action="'/api/' + url.upload"
          class="btn btn-sm btn-primary text-uppercase mr-1"
          @input-file="inputFile"
        >
          <b-icon class="mr-1" icon="upload" />
          Nahrať súbory
        </file-upload>
        <b-btn id="newFolderPop" size="sm">
          <b-icon class="mr-1" icon="folder-plus" />
          Vytvoriť zložku
        </b-btn>
        <b-popover
          target="newFolderPop"
          triggers="focus"
          @shown="focusFolderInput"
        >
          <b-form-input
            ref="folderInput"
            v-model="folder.name"
            v-validate="'required'"
            :class="{ 'is-invalid': errors.has('folder.name') }"
            name="foldername"
            placeholder="Názov zložky"
            @keydown.enter.native="createFolder"
          />
          <b-form-invalid-feedback>
            {{ errors.first('folder.name') }}
          </b-form-invalid-feedback>
        </b-popover>
      </div>
    </div>
    <server-table
      ref="table"
      :api-request="apiRequest"
      :busy.sync="table.loading"
      :columns="table.columns"
      :searchable="table.searchable"
      :sort-by.sync="table.sortBy"
      hover
      striped
      :selectable="$root.user.isAdmin"
      @selectedItems="onSelectedItems"
      @selectedRows="onSelectedRows"
    >
      <template #afterSearch>
        <transition-group
          :class="{ 'ml-3': table.searchable }"
          class="breadcrumb mb-0"
          name="fadeRightShort"
          tag="ol"
        >
          <b-breadcrumb-item key="home" @click.prevent="backbtn(0, 0)">
            <b-icon icon="house-fill" />
          </b-breadcrumb-item>
          <b-breadcrumb-item
            v-for="(breadcrumb, k) in breadcrumbs"
            :key="`bc-${k}`"
            @click.prevent="backbtn(breadcrumb.parent_id, k + 1)"
          >
            {{ breadcrumb.name }}
          </b-breadcrumb-item>
        </transition-group>
      </template>
      <template v-if="$root.user.isAdmin" #cell(actions)="data">
        <b-dropdown class="btn-p-0" no-caret dropright variant="link">
          <template slot="button-content">
            <b-icon icon="text-indent-left" scale="1.25x" />
          </template>
          <b-dropdown-item
            v-if="data.item.typeID === 2"
            :href="downloadFile(data.item.id)"
          >
            <b-icon class="mr-1" icon="download" />
            Stiahnuť
          </b-dropdown-item>
          <b-dropdown-item
            @click.prevent="renameF(data.item.id, data.item.name)"
          >
            <b-icon class="mr-1" icon="pencil-square" />
            Premenovať
          </b-dropdown-item>
          <b-dropdown-item @click.prevent="openExplorerModal([data.item])">
            <b-icon class="mr-1" icon="box-arrow-right" />
            Presunúť
          </b-dropdown-item>
          <b-dropdown-item
            link-class="text-danger"
            @click.prevent="deleteE([data.item.id])"
          >
            <b-icon class="mr-1" icon="trash" />
            Vymazať
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template #cell(name)="data">
        <template v-if="data.item.typeID == 1">
          <template v-if="!rename || rename !== data.item.id">
            <b-link @click.prevent="changeDir(data.item.id, data.value)">
              <img class="mr-2" src="/images/folder.png" /><strong>{{
                data.value
              }}</strong>
              <sup> ({{ data.item.filesCount }}) </sup>
            </b-link>
          </template>
        </template>
        <template v-else>
          <b-link
            v-if="!rename || rename !== data.item.id"
            :href="openFile(data.item.id)"
            target="_blank"
          >
            <img
              :src="'/images/' + data.item.extension + '.png'"
              class="mr-2"
            /><strong>{{ data.value }}</strong>
          </b-link>
        </template>
        <div
          v-if="data.item.note"
          class="fs-sm text-muted px-3 py-2 mt-1"
          v-html="$options.filters.nl2br(data.item.note)"
        />
        <b-form-input
          v-if="rename === data.item.id"
          v-model="renameValue"
          v-validate="'required'"
          :class="{ 'is-invalid': errors.has('renameValue') }"
          name="rename"
          @keydown.enter.native="renameE(data.item.id)"
        />
      </template>
      <template #selectActions="data">
        <b-dropdown :disabled="!data.selected.length" dropup size="sm">
          <template slot="button-content">
            <b-icon icon="gear-wide" class="mr-1" />
            Akcie
          </template>
          <b-dropdown-item
            v-if="url.downloadFiles"
            @click.prevent="downloadFiles(selectedItems)"
          >
            <b-icon class="mr-1" icon="download" />
            Stiahnuť
          </b-dropdown-item>
          <b-dropdown-item @click.prevent="openExplorerModal(selectedRows)">
            <b-icon class="mr-1" icon="box-arrow-right" />
            Presunúť
          </b-dropdown-item>
          <b-dropdown-item
            link-class="text-danger"
            @click.prevent="deleteE(selectedItems, true)"
          >
            <b-icon class="mr-1" icon="trash" />
            Vymazať
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </server-table>
    <modal
      ref="addAttachment"
      cancel-title="Zrušiť"
      name="add-attachment"
      ok-title="Nahrať"
      size="lg"
      title="Nahrať súbory"
      @hidden="closeModal"
      @ok="uploadFiles"
    >
      <div class="table-responsive">
        <table class="table table-striped table-bordered table-middle">
          <thead>
            <tr>
              <th>Názov</th>
              <th class="th-shrink">Veľkosť</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(file, i) in files" :key="i">
              <td class="position-relative py-2 font-weight-bold">
                <div
                  :style="{ width: file.progress + '%' }"
                  class="upload-progress"
                />
                <span style="position: relative; z-index: 1">{{
                  file.name
                }}</span>
              </td>
              <td class="td-shrink">
                {{ file.size | fileSize }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </modal>
    <file-explorer
      ref="moveExplorer"
      action-title="Presunúť"
      :url="{ list: url.explorer, action: url.move }"
    />
  </div>
</template>
<script>
import ServerTable from '@/components/ServerTable'
import Modal from '@/components/Modal'
import FileUpload from 'vue-upload-component'
import FileExplorer from './Explorer'

export default {
  components: {
    ServerTable,
    Modal,
    FileUpload,
    FileExplorer,
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      },
    },
    url: {
      type: Object,
      required: true,
    },
    name: String,
    table: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      folder: {
        name: '',
        parent_id: 0,
      },
      files: [],
      rename: false,
      renameValue: '',
      params: {
        parent_id: 0,
      },
      breadcrumbs: [],
      selectedItems: [],
      selectedRows: [],
      loading: false,
    }
  },
  watch: {
    params: {
      deep: true,
      handler() {
        this.$refs.table.refresh()
      },
    },
    files(file) {
      if (file.length > 0) {
        this.$root.$emit('modal-show', 'add-attachment')
      }
    },
  },
  mounted() {
    this.$root.$on('filemanager/reloadList', this.$refs.table.refresh)
  },
  beforeDestroy() {
    this.$root.$off('filemanager/reloadList')
  },
  methods: {
    apiRequest(params) {
      return this.$api.get(this.url.show, {
        params: Object.assign(this.params, params),
      })
    },
    onLoaded() {
      this.loading = false
    },
    onSelectedItems(items) {
      this.selectedItems = items
    },
    onSelectedRows(items) {
      this.selectedRows = items
    },
    focusFolderInput() {
      this.$refs.folderInput.focus()
    },
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        if (this.files.every((item) => item.success === true)) {
          setTimeout(() => {
            this.closeModal()
          }, 1000)
        }
        // Uploaded successfully
        /*if (newFile.success !== oldFile.success) {

        }*/
      }
    },
    createFolder() {
      if (this.folder.name) {
        this.$api
          .post(this.url.createFolder, Object.assign(this.data, this.folder))
          .then(() => {
            this.$refs.table.refresh()
            this.folder.name = ''
          })
      }
    },
    renameF(id, name) {
      this.rename = id
      this.renameValue = name
    },
    renameE(id) {
      this.$api
        .post(this.url.rename, {
          id: id,
          name: this.renameValue,
        })
        .then(() => {
          this.rename = false
          this.renameValue = ''
          this.$refs.table.refresh()
        })
    },
    downloadFiles(items) {
      this.$api
        .post(this.url.downloadFiles, { id: items }, { responseType: 'blob' })
        .then((res) => {
          saveAs(new Blob([res.data]), 'dokumenty.zip')
        })
    },
    deleteE(id, multi = false) {
      this.$swal({
        title: 'Ste si istý?',
        text: !multi
          ? 'Naozaj chcete odstrániť túto položku?'
          : `Naozaj chcete odstrániť tieto (${id.length}) položky?`,
        type: 'info',
        position: 'top',
        showConfirmButton: true,
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$api
            .put(this.url.delete, {
              id: id,
            })
            .then(() => {
              this.$refs.table.refresh()
            })
        }
      })
    },
    openExplorerModal(items) {
      this.$refs.moveExplorer.sourceItems = items
      this.$nextTick(() => {
        this.$root.$emit('modal-show', 'file-explorer')
      })
    },
    closeModal() {
      this.$refs.upload.clear()
      this.$refs.table.refresh()
      this.$root.$emit('modal-hide', 'add-attachment')
    },
    uploadFiles(e) {
      e.preventDefault()
      this.$refs.upload.active = true
    },
    changeDir(id, name) {
      this.breadcrumbs.push({
        parent_id: id,
        name: name,
      })
      this.params.parent_id = id
      this.folder.parent_id = id
    },
    backbtn(id, key) {
      this.params.parent_id = id
      this.folder.parent_id = id
      this.breadcrumbs = this.breadcrumbs.slice(0, key)
    },
    openFile(id) {
      return this.url.open + id
    },
    downloadFile(id) {
      return this.url.download + id
    },
  },
}
</script>
